import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Minus, Plus, Trash2, ShoppingBag, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { useCart, DELIVERY_FEE } from "../context/CartContext";
import { createCheckout, rupee, px } from "../lib/api";
import { BRAND } from "../lib/brand";

const STEPS = ["Cart", "Address", "Payment"];

export default function Checkout() {
  const { list, subtotal, total, count, setQuantity, removeItem } = useCart();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [address, setAddress] = useState({
    name: "", phone: "", line1: "", line2: "", city: "Noida", pincode: "", notes: "",
  });
  const navigate = useNavigate();

  if (count === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-5 text-center" data-testid="checkout-empty">
        <ShoppingBag className="w-12 h-12 text-orange" />
        <p className="font-heading uppercase text-4xl mt-4">Your cart is empty</p>
        <p className="text-cream/60 mt-2">Add some kukkad before checking out.</p>
        <Link to="/menu" data-testid="checkout-empty-menu" className="mt-6 bg-orange text-primary-foreground font-bold uppercase px-8 py-3.5 rounded-md hover:scale-105 transition-transform">
          Browse Menu
        </Link>
      </div>
    );
  }

  const validateAddress = () => {
    const { name, phone, line1, pincode } = address;
    if (!name || !phone || !line1 || !pincode) {
      toast.error("Please fill in name, phone, address and pincode");
      return false;
    }
    if (!/^\d{6}$/.test(pincode)) {
      toast.error("Enter a valid 6-digit pincode");
      return false;
    }
    return true;
  };

  const next = () => {
    if (step === 1 && !validateAddress()) return;
    setStep((s) => Math.min(s + 1, 2));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const pay = async () => {
    setLoading(true);
    try {
      const payload = {
        items: list.map((i) => ({ id: i.id, quantity: i.quantity, kind: i.kind })),
        address,
        origin_url: window.location.origin,
      };
      const res = await createCheckout(payload);
      if (res.checkout_url) {
        window.location.href = res.checkout_url;
      } else {
        throw new Error("No checkout URL");
      }
    } catch (e) {
      toast.error("Could not start payment. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div data-testid="checkout-page" className="min-h-screen bg-background">
      {/* Minimal header */}
      <header className="border-b border-border">
        <div className="max-w-[1000px] mx-auto px-5 sm:px-8 h-[74px] flex items-center justify-between">
          <Link to="/" data-testid="checkout-logo" className="flex items-center gap-2.5">
            <img src={BRAND.mascot} alt={BRAND.name} className="h-10 w-10 object-contain rounded-lg" />
            <span className="font-heading uppercase text-cream text-lg tracking-tight">the <span className="text-orange">kukkad</span> co.</span>
          </Link>
          <Link to="/menu" data-testid="checkout-back-menu" className="flex items-center gap-2 text-cream/60 hover:text-orange text-sm font-bold uppercase transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Menu
          </Link>
        </div>
      </header>

      {/* Stepper */}
      <div className="max-w-[1000px] mx-auto px-5 sm:px-8 pt-10">
        <div className="flex items-center gap-3">
          {STEPS.map((label, i) => (
            <React.Fragment key={label}>
              <div className="flex items-center gap-2" data-testid={`step-${i}`}>
                <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                  i < step ? "bg-orange text-primary-foreground" : i === step ? "bg-orange text-primary-foreground" : "bg-card text-cream/40 border border-border"
                }`}>
                  {i < step ? <Check className="w-4 h-4" /> : i + 1}
                </span>
                <span className={`uppercase text-sm font-bold ${i === step ? "text-cream" : "text-cream/40"}`}>{label}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-px ${i < step ? "bg-orange" : "bg-border"}`} />}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="max-w-[1000px] mx-auto px-5 sm:px-8 py-10 grid lg:grid-cols-3 gap-8 items-start">
        <div className="lg:col-span-2">
          <AnimatePresence mode="wait">
            {step === 0 && (
              <motion.div key="cart" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} data-testid="checkout-cart-step">
                <h1 className="font-heading uppercase text-4xl tracking-tighter mb-6">Review your order</h1>
                <div className="space-y-4">
                  {list.map((item) => (
                    <div key={item.key} data-testid={`checkout-item-${item.id}`} className="flex gap-4 items-center bg-card border border-border rounded-lg p-4">
                      {item.image && <img src={px(item.image, 200)} alt={item.name} className="w-16 h-16 rounded-md object-cover" />}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold truncate">{item.name}</p>
                        <p className="text-orange font-bold text-sm">{rupee(item.price)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => setQuantity(item.key, item.quantity - 1)} className="w-8 h-8 rounded bg-brown-light flex items-center justify-center hover:bg-orange hover:text-brown transition-colors"><Minus className="w-4 h-4" /></button>
                        <span className="w-6 text-center font-bold">{item.quantity}</span>
                        <button onClick={() => setQuantity(item.key, item.quantity + 1)} className="w-8 h-8 rounded bg-brown-light flex items-center justify-center hover:bg-orange hover:text-brown transition-colors"><Plus className="w-4 h-4" /></button>
                      </div>
                      <button onClick={() => removeItem(item.key)} className="text-cream/40 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {step === 1 && (
              <motion.div key="addr" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} data-testid="checkout-address-step">
                <h1 className="font-heading uppercase text-4xl tracking-tighter mb-6">Delivery details</h1>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Full name" testid="addr-name" value={address.name} onChange={(v) => setAddress({ ...address, name: v })} placeholder="e.g. Aarav Mehta" />
                  <Field label="Phone" testid="addr-phone" value={address.phone} onChange={(v) => setAddress({ ...address, phone: v })} placeholder="10-digit mobile" />
                  <div className="sm:col-span-2">
                    <Field label="Flat / House / Building" testid="addr-line1" value={address.line1} onChange={(v) => setAddress({ ...address, line1: v })} placeholder="Tower & flat no." />
                  </div>
                  <div className="sm:col-span-2">
                    <Field label="Area / Landmark" testid="addr-line2" value={address.line2} onChange={(v) => setAddress({ ...address, line2: v })} placeholder="Sector, landmark (optional)" />
                  </div>
                  <Field label="City" testid="addr-city" value={address.city} onChange={(v) => setAddress({ ...address, city: v })} />
                  <Field label="Pincode" testid="addr-pincode" value={address.pincode} onChange={(v) => setAddress({ ...address, pincode: v })} placeholder="201301" />
                  <div className="sm:col-span-2">
                    <Label className="uppercase text-xs tracking-widest text-cream/60">Delivery notes</Label>
                    <Textarea data-testid="addr-notes" value={address.notes} onChange={(e) => setAddress({ ...address, notes: e.target.value })} placeholder="Ring the bell, leave at door, etc." rows={3} className="mt-2 bg-brown-deep border-border text-cream focus-visible:ring-orange" />
                  </div>
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="pay" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} data-testid="checkout-payment-step">
                <h1 className="font-heading uppercase text-4xl tracking-tighter mb-6">Payment</h1>
                <div className="bg-card border border-border rounded-lg p-6">
                  <p className="text-cream/80">Delivering to</p>
                  <p className="font-semibold mt-1">{address.name} · {address.phone}</p>
                  <p className="text-cream/70 text-sm">{address.line1}{address.line2 ? `, ${address.line2}` : ""}, {address.city} - {address.pincode}</p>
                </div>
                <div className="mt-5 rounded-lg border border-orange/40 bg-orange/5 p-5">
                  <p className="text-cream/90">You’ll be redirected to a secure Stripe checkout to complete your payment. Your order confirms automatically once payment succeeds.</p>
                  <p className="text-cream/50 text-sm mt-3">Test card: 4242 4242 4242 4242 · any future expiry · any CVC</p>
                </div>
                <Button
                  data-testid="checkout-pay-button"
                  onClick={pay}
                  disabled={loading}
                  className="w-full mt-6 bg-orange text-primary-foreground font-bold uppercase text-base py-6 hover:scale-[1.01] transition-transform disabled:opacity-70"
                >
                  {loading ? (<><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Redirecting…</>) : (<>Pay {rupee(total)} securely</>)}
                </Button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Nav buttons */}
          {step < 2 && (
            <div className="flex justify-between mt-8">
              {step > 0 ? (
                <Button variant="ghost" onClick={back} data-testid="checkout-prev" className="text-cream/70 hover:text-cream uppercase font-bold">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
              ) : <span />}
              <Button onClick={next} data-testid="checkout-next" className="bg-orange text-primary-foreground font-bold uppercase px-8 hover:scale-105 transition-transform">
                {step === 0 ? "Add Address" : "Go to Payment"} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </div>

        {/* Summary */}
        <aside className="bg-brown-deep border border-border rounded-lg p-6 lg:sticky lg:top-6" data-testid="checkout-summary">
          <h3 className="font-heading uppercase text-2xl tracking-tight mb-4">Summary</h3>
          <div className="space-y-2 max-h-56 overflow-y-auto no-scrollbar">
            {list.map((i) => (
              <div key={i.key} className="flex justify-between text-sm text-cream/80">
                <span className="truncate mr-2">{i.quantity} × {i.name}</span>
                <span className="shrink-0">{rupee(i.price * i.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-border mt-4 pt-4 space-y-2">
            <div className="flex justify-between text-cream/70 text-sm"><span>Subtotal</span><span>{rupee(subtotal)}</span></div>
            <div className="flex justify-between text-cream/70 text-sm"><span>Delivery</span><span>{rupee(DELIVERY_FEE)}</span></div>
            <div className="flex justify-between font-heading text-2xl uppercase pt-2 border-t border-border">
              <span>Total</span><span className="text-orange" data-testid="checkout-total">{rupee(total)}</span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <Label className="uppercase text-xs tracking-widest text-cream/60">{label}</Label>
      <Input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-2 bg-brown-deep border-border text-cream focus-visible:ring-orange"
      />
    </div>
  );
}
